import { createContext, useReducer, ReactNode, Dispatch, useEffect, useRef, useState, useCallback } from 'react'
import { initialState, formReducer, FormState, FormAction } from './formReducer'
import { useSearchParams, useLocation } from 'react-router'
import { apiGet } from '../services/apiService'
import usePersistedNavigation from '@/hooks/usePersistedNavigation'
import { trackGeneralEvents } from '@/utils/gtm'
import { ROUTES } from '@/config/routes'
import { Dialog, DialogBackdrop, DialogPanel, DialogTitle, Description } from '@headlessui/react'
import { Button } from '@/components/Button'
import Input from '@/components/Input'
import { subYears, format } from 'date-fns'
import { XMarkIcon } from '@heroicons/react/24/outline'
import { CheckCircleIcon } from '@heroicons/react/24/solid'
import { PageLoader } from '@/components/PageLoader'
import { APPLICATION_ALREADY_COMPLETED_MESSAGE } from '@/constants/applicationMessages'

function isApplicationAlreadyCompleted (error: unknown): boolean {
  return error instanceof Error && error.message === APPLICATION_ALREADY_COMPLETED_MESSAGE
}

interface FormContextProps {
  state: FormState
  dispatch: Dispatch<FormAction>
}

interface FormProviderProps {
  children: ReactNode
}

const FormContext = createContext<FormContextProps | undefined>(undefined)

const FormProvider = ({ children }: FormProviderProps) => {
  const [state, dispatch] = useReducer(formReducer, initialState)
  const [searchParams] = useSearchParams()
  const location = useLocation()
  const navigate = usePersistedNavigation()
  const hasNavigatedRef = useRef(false)
  const hasCheckedDobRef = useRef(false)

  const [isDobModalOpen, setIsDobModalOpen] = useState(false)
  const [dobInput, setDobInput] = useState('') // yyyy-MM-dd from native date input
  const [dobError, setDobError] = useState<string | null>(null)
  const [isDobSubmitting, setIsDobSubmitting] = useState(false)
  const [isCheckingDobNeeded, setIsCheckingDobNeeded] = useState(false)
  const [isAlreadyCompletedOpen, setIsAlreadyCompletedOpen] = useState(false)

  // Use same DOB constraints as YourDetails page
  const minDateOfBirth = format(subYears(new Date(), 100), 'yyyy-MM-dd')
  const maxDateOfBirth = format(subYears(new Date(), 18), 'yyyy-MM-dd')

  const fetchSavedState = useCallback(async (formId: string, dob?: string) => {
    try {
      const url = `Form/GetById?id=${formId}${dob ? `&dob=${encodeURIComponent(dob)}` : ''}`
      const result = await apiGet(url)
      if (result.success && result.data) {
        const savedState = result.data

        // Update form metadata
        dispatch({
          type: 'UPDATE_FORM_METADATA',
          payload: {
            formId: savedState.formId || formId || null,
            dealerId: savedState.dealerId ? savedState.dealerId : null
          }
        })

        // If isPostCall is true, then update the form metadata with the post call data
        if (savedState.isPostCall) {
          dispatch({
            type: 'SET_IS_POST_CALL',
            payload: savedState.isPostCall
          })
        }

        // Update personal section if it exists
        if (savedState.personal) {
          if (savedState.personal.yourDetails && savedState.personal.yourDetails.firstName !== null) {
            dispatch({
              type: 'UPDATE_PERSONAL',
              step: 'yourDetails',
              payload: savedState.personal.yourDetails
            })
          }

          if (savedState.personal.aboutYou) {
            dispatch({
              type: 'UPDATE_PERSONAL',
              step: 'aboutYou',
              payload: savedState.personal.aboutYou
            })
          }

          if (savedState.personal.income) {
            dispatch({
              type: 'UPDATE_PERSONAL',
              step: 'income',
              payload: savedState.personal.income
            })
          }

          if (savedState.personal.financialPosition) {
            dispatch({
              type: 'UPDATE_PERSONAL',
              step: 'financialPosition',
              payload: savedState.personal.financialPosition
            })
          }

          if (savedState.personal.applicationOptions) {
            dispatch({
              type: 'UPDATE_PERSONAL',
              step: 'applicationOptions',
              payload: savedState.personal.applicationOptions
            })
          }
        }

        // Update business section if it exists and is not null
        if (savedState.business) {
          if (savedState.business.aboutYourBusiness) {
            dispatch({
              type: 'UPDATE_BUSINESS',
              step: 'aboutYourBusiness',
              payload: savedState.business.aboutYourBusiness
            })
          }

          if (savedState.business.aboutYou) {
            dispatch({
              type: 'UPDATE_BUSINESS',
              step: 'aboutYou',
              payload: savedState.business.aboutYou
            })
          }

          if (savedState.business.financialPosition) {
            dispatch({
              type: 'UPDATE_BUSINESS',
              step: 'financialPosition',
              payload: savedState.business.financialPosition
            })
          }
        }

        // Prevent navigation if on /dealer page
        if (location.pathname === ROUTES.DEALER.path) {
          return
        }

        if (hasNavigatedRef.current) return

        // If isPostCall is true, then navigate to the the selection page
        if (savedState.isPostCall) {
          hasNavigatedRef.current = true
          if (savedState.personal && savedState.business) {
            navigate(ROUTES.HOME.path)
          } else if (savedState.personal) {
            navigate(ROUTES.PERSONAL.YOUR_DETAILS.path)
          } else if (savedState.business) {
            navigate(ROUTES.BUSINESS.ABOUT_YOUR_BUSINESS.path)
          }
        } else {
          // Navigate based on saved data
          if (savedState.personal) {
            // Track application restart event
            trackGeneralEvents.applicationRestart()
            hasNavigatedRef.current = true
            navigate(ROUTES.PERSONAL.YOUR_DETAILS.path)
          } else if (savedState.business) {
            hasNavigatedRef.current = true
            navigate(ROUTES.BUSINESS.ABOUT_YOUR_BUSINESS.path)
          }
        }

      }
      return result
    } catch (error) {
      if (!isApplicationAlreadyCompleted(error)) {
        console.error('Error fetching saved form state:', error)
      }
      throw error
    }
  }, [location.pathname, navigate, dispatch])

  const showAlreadyCompletedDialog = () => {
    setIsDobModalOpen(false)
    setDobError(null)
    setDobInput('')
    setIsAlreadyCompletedOpen(true)
  }

  // IMPORTANT: This effect should only run when searchParams changes (e.g., on initial load or when formid changes).
  // DO NOT add location.pathname or other navigation-related dependencies here!
  // Adding location.pathname will cause this effect to re-run on every route change, which can result in unnecessary API calls
  // and override unsaved form data. Only searchParams should be a dependency unless you have a very good reason.
  useEffect(() => {
    const formId = searchParams.get('formid') || searchParams.get('formID') || searchParams.get('formId')
    // If there's no form id, ensure DOB modal is closed and reset check state
    if (!formId) {
      setIsDobModalOpen(false)
      setIsAlreadyCompletedOpen(false)
      setDobError(null)
      setDobInput('')
      hasCheckedDobRef.current = false
      return
    }
    if (hasCheckedDobRef.current) return
    const checkDob = async () => {
      setIsCheckingDobNeeded(true)
      try {
        const response = await apiGet(`Form/IsDobNeeded?id=${formId}`)
        const needDob = response.data || false
        hasCheckedDobRef.current = true
        if (needDob) {
          setIsDobModalOpen(true)
        } else {
          await fetchSavedState(formId)
        }
      } catch (error: unknown) {
        if (isApplicationAlreadyCompleted(error)) {
          hasCheckedDobRef.current = true
          showAlreadyCompletedDialog()
          return
        }
        console.error('Error checking DOB requirement:', error)
        hasCheckedDobRef.current = true
        try {
          await fetchSavedState(formId)
        } catch (savedStateError: unknown) {
          if (isApplicationAlreadyCompleted(savedStateError)) {
            showAlreadyCompletedDialog()
          }
        }
      } finally {
        setIsCheckingDobNeeded(false)
      }
    }
    checkDob()
  }, [searchParams])

  const handleDobSubmit = async () => {
    setDobError(null)
    const formId = searchParams.get('formid') || searchParams.get('formID') || searchParams.get('formId')
    if (!formId) {
      setIsDobModalOpen(false)
      return
    }
    if (!dobInput) {
      setDobError('Date of birth is required')
      return
    }
    // Validate range
    if (dobInput < minDateOfBirth || dobInput > maxDateOfBirth) {
      setDobError('Please enter a valid date of birth')
      return
    }
    try {
      setIsDobSubmitting(true)
      // Send yyyy-MM-dd to API
      const res = await fetchSavedState(formId, dobInput)
      if (res?.success) {
        setIsDobModalOpen(false)
      }
    } catch (err: unknown) {
      if (isApplicationAlreadyCompleted(err)) {
        showAlreadyCompletedDialog()
        return
      }
      if (err instanceof Error && err.message) {
        setDobError(err.message)
      } else {
        setDobError('Something went wrong. Please try again.')
      }
    } finally {
      setIsDobSubmitting(false)
    }
  }

  const handleStartNewApplication = () => {
    dispatch({ type: 'RESET' })
    // Ensure any open DOB modal is closed and internal flags reset
    setIsDobModalOpen(false)
    setDobError(null)
    setDobInput('')
    hasCheckedDobRef.current = false
    navigate(ROUTES.HOME.path, { removeKeys: ['formid', 'formID', 'formId'] })
  }

  const closeAndNavigateToHome = () => {
    setIsDobModalOpen(false)
    navigate(ROUTES.HOME.path, { removeKeys: ['formid', 'formID', 'formId'] })
  }

  const closeAlreadyCompletedAndGoHome = () => {
    setIsAlreadyCompletedOpen(false)
    setIsDobModalOpen(false)
    navigate(ROUTES.HOME.path, { removeKeys: ['formid', 'formID', 'formId'] })
  }

  return (
    <FormContext.Provider value={{ state, dispatch }}>
      {children}

      <PageLoader open={isCheckingDobNeeded} />

      <Dialog open={isDobModalOpen} onClose={() => { }} className='relative z-30'>
        <DialogBackdrop
          transition
          className='fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in'
        />
        <div className='fixed inset-0 z-10 w-screen overflow-y-auto'>
          <div className='flex min-h-full items-center justify-center py-4 text-center sm:items-center sm:p-0'>
            <DialogPanel
              transition
              className='relative transform rounded-lg bg-white pt-5 pb-4 m-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-2xl sm:py-6 data-closed:sm:translate-y-0 data-closed:sm:scale-95'
            >
              <div className='flex items-center justify-between px-4 sm:px-6 pb-5 border-b border-[var(--form-box-divider)]'>
                <DialogTitle as='h3' className='text-2xl'>
                  Continue application
                </DialogTitle>
                <button
                  className='cursor-pointer transition duration-300 flex items-center justify-center rounded-full p-2 hover:bg-[var(--theme-bg)] hover:text-[var(--brand-1)] relative -right-4'
                  type='button'
                  onClick={() => closeAndNavigateToHome()}
                >
                  <XMarkIcon className='size-6' aria-hidden='true' />
                </button>
              </div>
              <div className='p-4 sm:p-6'>
                <Description className='text-lg mb-6'>
                  Please enter your date of birth to help us verify your information and continue with your application.
                </Description>
                <div className='mb-6'>
                  <Input
                    label='Date of birth'
                    inputId='dob'
                    required={true}
                    type='date'
                    min={minDateOfBirth}
                    max={maxDateOfBirth}
                    value={dobInput}
                    onChange={(e) => setDobInput((e.target as HTMLInputElement).value)}
                    error={dobError ? { message: dobError } : undefined}
                  />
                </div>
                <div className='text-center m-8'>
                  <button onClick={handleStartNewApplication} className='text-[var(--brand-2)] font-[var(--font-weight-bold)]'>Start a new application</button>
                </div>
                <div className='flex justify-center'>
                  <Button onClick={handleDobSubmit} disabled={isDobSubmitting}>
                    Next
                  </Button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>

      <Dialog open={isAlreadyCompletedOpen} onClose={closeAlreadyCompletedAndGoHome} className='relative z-30'>
        <DialogBackdrop
          transition
          className='fixed inset-0 bg-gray-500/75 transition-opacity data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in'
        />
        <div className='fixed inset-0 z-10 w-screen overflow-y-auto'>
          <div className='flex min-h-full items-end justify-center py-4 text-center sm:items-center sm:p-0'>
            <DialogPanel
              transition
              className='relative transform rounded-lg bg-white pt-6 pb-4 text-left shadow-xl transition-all data-closed:translate-y-4 data-closed:opacity-0 data-enter:duration-300 data-enter:ease-out data-leave:duration-200 data-leave:ease-in sm:my-8 sm:w-full sm:max-w-xs data-closed:sm:translate-y-0 data-closed:sm:scale-95'
            >
              <div>
                <div className='p-4 sm:p-6'>
                  <div className='flex flex-col gap-4 items-center justify-center'>
                    <div className=''>
                      <CheckCircleIcon className='size-24 text-[var(--theme-success)]' aria-hidden='true' />
                    </div>
                    <DialogTitle as='h3' className='text-2xl text-center mt-1'>
                      Your application has been completed
                    </DialogTitle>
                    <p className='text-base text-center -mt-2.5'>
                      Thank you for your application. We have received your application and a member of our team will contact your soon.
                    </p>
                    <Button icon='none' className='w-full mt-4' onClick={closeAlreadyCompletedAndGoHome}>
                      Return to home
                    </Button>
                  </div>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </FormContext.Provider>
  )
}

export { FormContext, FormProvider }
